import React, { useState, useEffect } from 'react';
import { EnvelopeIcon, ExclamationTriangleIcon, CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import emailHealthService, { EmailHealthStats } from '../../services/emailHealthService';
import toast from 'react-hot-toast';

const EmailHealthCard: React.FC = () => {
  const [stats, setStats] = useState<EmailHealthStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEmailHealth();
  }, []);

  const fetchEmailHealth = async () => {
    try {
      setLoading(true);
      const healthData = await emailHealthService.getEmailHealthStats();

      setStats(healthData);
    } catch (error) {
      console.error('Failed to fetch email health:', error);
      toast.error('Failed to load email health data');
    } finally {
      setLoading(false);
    }
  };

  const getHealthColor = (rate: number) => {
    if (rate < 2) return 'text-green-600 bg-green-100';
    if (rate < 5) return 'text-blue-600 bg-blue-100';
    if (rate < 10) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getHealthIcon = (rate: number) => {
    if (rate < 5) return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
    if (rate < 10) return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
    return <XMarkIcon className="w-5 h-5 text-red-500" />;
  };

  if (loading) {
    return (
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <EnvelopeIcon className="h-6 w-6 text-gray-400" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Email Health</dt>
                <dd className="text-lg font-medium text-gray-900">Loading...</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <EnvelopeIcon className="h-6 w-6 text-gray-400" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Email Health</dt>
                <dd className="text-lg font-medium text-gray-900">No data available</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <EnvelopeIcon className="h-6 w-6 text-gray-400" />
            </div>
            <div className="ml-5">
              <dt className="text-sm font-medium text-gray-500 truncate">Email Health</dt>
              <div className="flex items-center space-x-2">
                {getHealthIcon(stats.unsubscribe_rate)}
                <dd className={`text-lg font-medium px-2 py-1 rounded-full ${getHealthColor(stats.unsubscribe_rate)}`}>
                  {stats.unsubscribe_rate.toFixed(1)}% Unsubscribed
                </dd>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Stats */}
        <div className="mt-4 grid grid-cols-4 gap-4">
          <div className="text-center">
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Total
            </dt>
            <dd className="mt-1 text-lg font-semibold text-gray-900">
              {stats.total.toLocaleString()}
            </dd>
          </div>
          <div className="text-center">
            <dt className="text-xs font-medium text-green-500 uppercase tracking-wider">
              Subscribed
            </dt>
            <dd className="mt-1 text-lg font-semibold text-green-600">
              {stats.subscribed.toLocaleString()}
            </dd>
          </div>
          <div className="text-center">
            <dt className="text-xs font-medium text-red-500 uppercase tracking-wider">
              Unsubscribed
            </dt>
            <dd className="mt-1 text-lg font-semibold text-red-600">
              {stats.unsubscribed.toLocaleString()}
            </dd>
          </div>
          <div className="text-center">
            <dt className="text-xs font-medium text-yellow-500 uppercase tracking-wider">
              Bounced
            </dt>
            <dd className="mt-1 text-lg font-semibold text-yellow-600">
              {stats.bounced.toLocaleString()}
            </dd>
          </div>
        </div>


        {/* Health Recommendation */}
        {stats.unsubscribe_rate > 5 && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex">
              <ExclamationTriangleIcon className="h-4 w-4 text-yellow-400 mt-0.5" />
              <div className="ml-2">
                <h3 className="text-sm font-medium text-yellow-800">
                  High unsubscribe rate detected
                </h3>
                <div className="mt-1 text-sm text-yellow-700">
                  Consider reviewing your email content and frequency to improve engagement.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailHealthCard;